import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Zojuist / Just now';
  if (m < 60) return `${m}m geleden / ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}u geleden / ago`;
  const d = Math.floor(h / 24);
  return `${d}d geleden / ago`;
}

export default function BusinessActivityScreen({ lang, businessId, currentUserId }) {
  const nl = lang === 'nl';
  const [log, setLog] = useState([]);
  const [filter, setFilter] = useState('all'); // all | program | training

  useEffect(() => {
    const key = `sl_biz_activity_${businessId}_${currentUserId}`;
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      setLog(stored);
    } catch { setLog([]); }
  }, [businessId, currentUserId]);

  const clearLog = () => {
    if (!window.confirm(nl ? 'Activiteitenlog wissen?' : 'Clear activity log?')) return;
    const key = `sl_biz_activity_${businessId}_${currentUserId}`;
    localStorage.removeItem(key);
    setLog([]);
  };

  const filtered = filter === 'all' ? log
    : filter === 'program' ? log.filter(e => e.type !== 'training')
    : log.filter(e => e.type === 'training');

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{nl ? 'Activiteit' : 'Activity'}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>
            {log.length} {nl ? 'activiteiten bijgehouden' : 'activities tracked'}
          </div>
        </div>
        {log.length > 0 && (
          <button onClick={clearLog}
            style={{ background: 'none', border: `1px solid ${C.red}44`, color: C.red, borderRadius: 8, fontSize: 11, padding: '5px 10px', cursor: 'pointer' }}>
            {nl ? 'Wissen' : 'Clear'}
          </button>
        )}
      </div>

      {/* Stats strip */}
      {log.length > 0 && (() => {
        const today = new Date().toDateString();
        const todayCount = log.filter(e => new Date(e.time).toDateString() === today).length;
        const weekAgo = Date.now() - 7 * 86400000;
        const weekCount = log.filter(e => e.time > weekAgo).length;
        return (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            {[
              { label: nl ? 'Vandaag' : 'Today', value: todayCount, color: _T.accent },
              { label: nl ? '7 dagen' : '7 days', value: weekCount, color: '#5B9ED0' },
              { label: nl ? 'Totaal' : 'Total', value: log.length, color: '#9B59B6' },
            ].map(stat => (
              <div key={stat.label} style={{ flex: 1, background: _T.bg3, borderRadius: 12, padding: '12px 10px', border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
                <div style={{ fontSize: 20, color: stat.color, fontWeight: 700 }}>{stat.value}</div>
                <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Filter pills */}
      {log.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[['all', nl ? 'Alles' : 'All'], ['program', nl ? "Programma's" : 'Programs'], ['training', nl ? 'Training' : 'Training']].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              style={{ background: filter === id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg3, border: `1px solid ${filter === id ? 'transparent' : _T.cardBorderBot}`, borderRadius: 20, color: filter === id ? '#0a1220' : C.silver, fontSize: 11, fontWeight: 700, padding: '5px 12px', cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {log.length === 0 && (
        <div style={{ textAlign: 'center', padding: '56px 20px', color: C.silver2 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏃</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: C.silver }}>{nl ? 'Nog geen activiteiten' : 'No activities yet'}</div>
          <div style={{ fontSize: 13, color: C.silver2, lineHeight: 1.6 }}>
            {nl
              ? "Voltooi taken in een programma of trainingsmodule om activiteiten bij te houden."
              : "Complete tasks in a program or training module to start tracking activities."}
          </div>
        </div>
      )}

      {/* Activity log */}
      {filtered.map((entry, i) => {
        const isFirst = i === 0 || new Date(filtered[i - 1]?.time).toDateString() !== new Date(entry.time).toDateString();
        const dateLabel = (() => {
          const d = new Date(entry.time);
          const today = new Date().toDateString();
          const yesterday = new Date(Date.now() - 86400000).toDateString();
          if (d.toDateString() === today) return nl ? 'Vandaag' : 'Today';
          if (d.toDateString() === yesterday) return nl ? 'Gisteren' : 'Yesterday';
          return d.toLocaleDateString(nl ? 'nl-NL' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        })();
        return (
          <React.Fragment key={i}>
            {isFirst && (
              <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 8, marginTop: i === 0 ? 0 : 16 }}>
                {dateLabel.toUpperCase()}
              </div>
            )}
            <div style={{ background: _T.bg3, borderRadius: 14, padding: '12px 14px', border: `1px solid ${_T.cardBorderBot}`, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${_T.accent}18`, border: `1px solid ${_T.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                {entry.type === 'training' ? '🧠' : '✅'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{entry.task}</div>
                {entry.program && (
                  <div style={{ color: _T.accent, fontSize: 11, fontWeight: 600 }}>
                    {entry.type === 'training' ? '🧩 ' : '📋 '}{entry.program}
                  </div>
                )}
                <div style={{ color: C.silver, fontSize: 10, marginTop: 3 }}>{timeAgo(entry.time)}</div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
